import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import api from "@/utils/api";

const AuthContext = createContext(null);

// Convert https://x/api → wss://x/api ; http://x → ws://x
const wsUrlFor = (path) => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return base.replace(/^http/i, "ws") + path;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rolesChangedAt, setRolesChangedAt] = useState(0);
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);

  // ---- WebSocket: live-push permission changes so users never need to relog.
  const connectWS = useCallback(() => {
    const token = localStorage.getItem("ampl_token");
    if (!token) return;
    try {
      const ws = new WebSocket(wsUrlFor(`/api/ws/permissions?token=${encodeURIComponent(token)}`));
      wsRef.current = ws;
      ws.onopen = () => {
        // Ping every 25s to keep the connection alive.
        pingRef.current = setInterval(() => {
          try { ws.send("ping"); } catch { /* ignore */ }
        }, 25000);
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "features_updated" && Array.isArray(msg.enabled_features)) {
            setUser((prev) => {
              if (!prev) return prev;
              const next = { ...prev, enabled_features: msg.enabled_features };
              localStorage.setItem("ampl_user", JSON.stringify(next));
              return next;
            });
          } else if (msg.type === "role_list_updated") {
            // Bump a counter — admin pages listen for this and re-fetch.
            setRolesChangedAt(Date.now());
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
        // Reconnect after 3s (only if user is still logged in).
        if (localStorage.getItem("ampl_token")) {
          reconnectRef.current = setTimeout(connectWS, 3000);
        }
      };
      ws.onerror = () => { /* onclose will handle cleanup */ };
    } catch {
      /* WS support absent or blocked — permission changes still land on next reload */
    }
  }, []);

  const disconnectWS = useCallback(() => {
    if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* ignore */ }
      wsRef.current = null;
    }
  }, []);

  // Validate any cached token on app load. If invalid/expired, clear it.
  useEffect(() => {
    const validate = async () => {
      const token = localStorage.getItem("ampl_token");
      const savedUser = localStorage.getItem("ampl_user");
      if (!token || !savedUser) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        localStorage.setItem("ampl_user", JSON.stringify(res.data));
      } catch {
        localStorage.removeItem("ampl_token");
        localStorage.removeItem("ampl_user");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, []);

  // Manage WS lifecycle around user state.
  useEffect(() => {
    if (user) connectWS(); else disconnectWS();
    return () => disconnectWS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const login = async (userId, password) => {
    localStorage.removeItem("ampl_token");
    localStorage.removeItem("ampl_user");
    const res = await api.post("/auth/login", { user_id: userId, password });
    const { token, user: userData } = res.data;
    localStorage.setItem("ampl_token", token);
    localStorage.setItem("ampl_user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    disconnectWS();
    localStorage.removeItem("ampl_token");
    localStorage.removeItem("ampl_user");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider
      value={{
        user, loading, login, logout, rolesChangedAt,
        isFeatureEnabled: (key) => user?.role === "Main Admin" || (user?.enabled_features || []).includes(key),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};
